import React from "react";
import loadable from "@loadable/component";
import pMinDelay from "p-min-delay";
import Loader from "./shared/Loader";

// Layouts
import Layout2 from "./shared/layouts/Layout2";
import LayoutBlank from "./shared/layouts/LayoutBlank";
import { getDomainInfo } from "./utils/userAccess";

// Lazy load component
const lazy = (cb) => {
  return loadable(() => {
    return new Promise((resolve, reject) => {
      const loadComponent = () => {
        return pMinDelay(cb(), 200)
          .then(resolve)
          .catch(error => {
            console.error('Chunk loading error:', error);
            
            // If it's a chunk loading error, try to reload
            if (error.message && error.message.includes('Loading chunk')) {
              console.log('Attempting to reload chunk...');
              
              // Clear cache entries that might be causing issues
              if ('caches' in window) {
                caches.keys().then(cacheNames => {
                  cacheNames.forEach(cacheName => {
                    caches.delete(cacheName);
                  });
                });
              }
              
              // Try again after a short delay
              setTimeout(loadComponent, 1000);
            } else {
              reject(error);
            }
          });
      };
      
      loadComponent();
    });
  }, { 
    fallback: <Loader />,
    timeout: 10000 // 10 second timeout
  });
};
// ---
// Default application layout

export const DefaultLayout = Layout2;
// ---
// Document title template
const domainInfo = getDomainInfo();
export const titleTemplate = domainInfo.domain === "Goldsainte"
  ? "%s - GoldSainte.com"
  : domainInfo.domain === "TravelThru"
    ? "%s - TravelThru.com"
    : "%s - ChauffeurBookingSoftware.com";
// ---
// Routes
//
// Note: By default all routes use { "exact": true }. To change this
// behaviour, pass "exact" option explicitly to the route object
export const defaultRoute = "/";//dispatch?h=24";
export const routes = [
  {
    path: '/mcp-connect',
    component: lazy(() => import('./components/pages/quotations/McpConnectPage.tsx')),
    isPublic: true,
    layout: LayoutBlank,
  },
  {
    path: '/',
    component: lazy(() => import('./components/Default.tsx')),
    isPublic: true,
    layout: LayoutBlank,
  },
  {
    path: '/baocao/onlinegov',
    component: lazy(() => import('./components/pages/reports/OnlineGovReport.jsx')),
    isPublic: true,
    layout: LayoutBlank,
  },
  {
    path: '/boundary-designer',
    component: lazy(() => import('./components/pages/pricing/public/BoundaryDesignerPage.tsx')),
    isPublic: true,
    layout: LayoutBlank,
  },
  {
    path: "/bookings",
    component: lazy(() => import("./components/pages/clientPages/BookingsPage.tsx")),
  },
  {
    path: "/bookings/admin/:id?",
    component: lazy(() => import("./components/pages/dispatch/BookingDetailsAdminPage")),
  },
  {
    path: "/bookings/booking/:id?",
    component: lazy(() => import("./components/pages/dispatch/BookingDetails/BookingDetailsPage")),
  },
  {
    path: "/booking/payment/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/booking/BookingPaymentPage.tsx")),
    layout: LayoutBlank,
  },
  //http://localhost:3000/#/bookingdetails/10ed0154-f7cf-4c92-bab0-95062b4e7340
  {
    path: "/bookingdetails/:id?",
    isPublic: false,
    component: lazy(() => import("./components/pages/dispatch/BookingDetails/BookingDetails")),
    layout: LayoutBlank,
  },
  //http://localhost:3000/#/reservation/10ed0154-f7cf-4c92-bab0-95062b4e7340
  {
    path: "/reservation/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/ClientReservation/ClientReservationPage")),
    layout: LayoutBlank,
  },
  // Platform-hosted public booking page for a tenant: /#/bb/:websiteGuid searches, /#/bb/:websiteGuid/ride
  // picks the vehicle and takes payment. The website guid in the URL is the tenant — the widget passes it as
  // its API key and the server resolves vehicles, prices and currency from it.
  // Two separate entries because Router.js defaults every route to exact matching, so "/bb/:websiteGuid"
  // does not also match "/bb/<guid>/ride". The order below is just defensive.
  {
    path: "/bb/:websiteGuid/ride",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/BookingWidget/BookingWidgetPage")),
    layout: LayoutBlank,
  },
  // Legal documents for the public booking page. Generic templates branded with the operator's name, with
  // their own configured ride policies spliced in where set — see BookingLegalPage.
  {
    path: "/bb/:websiteGuid/terms",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/BookingWidget/BookingLegalPage")),
    layout: LayoutBlank,
  },
  {
    path: "/bb/:websiteGuid/privacy",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/BookingWidget/BookingLegalPage")),
    layout: LayoutBlank,
  },
  {
    path: "/bb/:websiteGuid",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/BookingWidget/BookingWidgetPage")),
    layout: LayoutBlank,
  },
  // Traveller booking page rendered by the booking-script customer widget (from public/booking-script).
  {
    path: "/cb/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/CustomerBooking/CustomerBookingPage")),
    layout: LayoutBlank,
  },
  // SMS alias of /cb/ — same page, one character shorter, and the path itself identifies the channel so the
  // open beacon doesn't need a "?s" query tag (2 more characters). SMS bodies are billed per 160-char segment
  // and sit right on that boundary, so characters here buy route text in the passenger's message.
  // NEVER remove /cb/: links already sent live in people's phones indefinitely.
  // See docs/cbs-admin/features/sms_roi_open_and_holdout.md.
  {
    path: "/s/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/CustomerBooking/CustomerBookingPage")),
    layout: LayoutBlank,
  },
  {
    path: "/voucher/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/ClientReservation/ClientVoucherPage")),
    layout: LayoutBlank,
  },
  {
    path: "/client-registration/:id?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/RegistrationPage.tsx")),
    layout: LayoutBlank,
  },
  {
    path: "/registration/:code?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/ClientRegistration/TravelAgentRegistration.tsx")),
    layout: LayoutBlank,
  },
  {
    path: "/finish-registration/:rid?",
    isPublic: true,
    component: lazy(() => import("./components/pages/clientPages/ClientRegistration/CompleteTravelAgentRegistration.tsx")),
    layout: LayoutBlank,
  },
  {
    path: "/book/:id?",
    component: lazy(() => import("./components/pages/clientPages/EditBookingPage")),
  },
  {
    path: "/driver-bookings",
    component: lazy(() => import("./components/pages/driver/DriverBookingsPage")),
  },
  {
    path: "/finished-bookings",
    component: lazy(() => import("./components/pages/booking/FinishedBookingsPage")),
  },
  {
    path: "/dbd/:hash",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/driver/BookingDetails/BookingDetailsPage")
    ),
  },
  {
    path: "/dbd-incident/:hash",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/driver/BookingDetails/WebProofUploadPage")
    ),
  },
  {
    path: "/dci/:token",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/driver/DriverInvitePage")
    ),
  },
  // Public bidding page for a quotation. Its own route and page, deliberately not a reuse of the
  // driver invite page: that one is single-use, single-driver and about one booking, this one is
  // many-use, many-bidder and about a whole itinerary.
  {
    path: "/q/:token",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/quotations/PublicQuotationPage")
    ),
  },
  {
    path: "/proposal/:token",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() => import("./components/pages/quotations/PublicClientProposalPage")),
  },
  {
    // Before "/quotations/:id", which would take "network" for an id.
    path: "/quotations/network",
    component: lazy(() => import("./components/pages/quotations/NetworkPage")),
  },
  {
    // Win/loss of the enquiries (owner, 2026-10-17); before "/quotations/:id" too.
    path: "/quotations/results",
    component: lazy(() => import("./components/pages/quotations/QuotationResultsPage")),
  },
  {
    // How quotations work, for dispatchers (owner, 2026-10-17); before "/quotations/:id" too.
    path: "/quotations/guide",
    component: lazy(() => import("./components/pages/quotations/QuotationGuidePage")),
  },
  {
    path: "/quotations/new",
    component: lazy(() => import("./components/pages/quotations/NewQuotationPage")),
  },
  {
    path: "/quotations/:id",
    component: lazy(() => import("./components/pages/quotations/QuotationDetailPage")),
  },
  {
    path: "/quotations",
    component: lazy(() => import("./components/pages/quotations/QuotationsPage")),
  },
  {
    path: "/dbdetails/:id?",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/driver/BookingDetails/BookingDetailsPage")
    ),
  },
  {
    path: "/booking/:id?",
    component: lazy(() => import("./components/pages/booking/EditBooking.jsx")),
  },
  {
    path: "/dispatch",
    component: lazy(() => import("./components/pages/dispatch/Dashboard.jsx")),
  },
  {
    path: "/live-map",
    component: lazy(() => import("./components/pages/liveMap/LiveMapPage.jsx")),
  },
  {
    path: "/suppliers",
    component: lazy(() =>
      import("./components/pages/suppliers/SuppliersPage.jsx")
    ),
  },
  {
    path: "/clients",
    component: lazy(() => import("./components/pages/clients/ClientsPage.jsx")),
  },
  {
    path: "/client/companies/:id?",
    component: lazy(() => import("./components/pages/clients/company/ClientCompaniesPage.tsx")),
  },
  {
    path: "/coupons",
    component: lazy(() => import("./components/pages/coupons/CouponsPage.jsx")),
  },
  {
    path: "/customer",
    component: lazy(() => import("./components/pages/Customer/CustomerPage.jsx")),
  },
  {
    path: "/auto-assign-rules",
    component: lazy(() => import("./components/pages/autoAssignRules/AutoAssignRulesPage.jsx")),
  },
  {
    path: "/carowners",
    component: lazy(() =>
      import("./components/pages/goldsainte/carOwners/CarOwners.jsx")
    ),
  },
  {
    path: "/vehicles",
    component: lazy(() =>
      import("./components/pages/goldsainte/vehicles/Vehicles.jsx")
    ),
  },
  {
    path: "/vehicles-types",
    component: lazy(() =>
      import("./components/pages/goldsainte/vehiclesTypes/VehiclesTypes.tsx")
    ),
  },
  {
    path: "/rentals",
    component: lazy(() =>
      import("./components/pages/goldsainte/carRentals/CarRentals.jsx")
    ),
  },
  {
    path: "/reports-bookings",
    component: lazy(() => import("./components/pages/reports/Performance.jsx")),
  },
  {
    path: "/rankings",
    component: lazy(() => import("./components/pages/reports/RankingsPage.jsx")),
  },
  {
    path: "/import-payment",
    component: lazy(() => import("./components/pages/reports/ImportPayment.jsx")),
  },
  {
    path: "/reports-booking",
    component: lazy(() => import("./components/pages/reports/BookingReports")),
  },
  {
    path: "/reports-booking-insights",
    component: lazy(() => import("./components/pages/reports/BookingInsightsReport")),
  },
  {
    path: "/operations",
    component: lazy(() => import("./components/pages/reports/Operations")),
  },
  {
    path: "/pickup-points",
    component: lazy(() => import("./components/pages/reports/Searches/PickupPoints")),
    isPublic: true,
  },
  {
    path: "/bookingdotcom-stats",
    component: lazy(() =>
      import("./components/pages/booking.com/Statistics.jsx")
    ),
  },
  {
    path: "/bookingdotcom-requests",
    component: lazy(() =>
      import("./components/pages/booking.com/Requests.jsx")
    ),
  },
  {
    // Public anonymous link (no login required, no sidenav) — backward compatible
    path: "/airport-pickups/:websiteId/:airportCode?",
    isPublic: true,
    layout: LayoutBlank,
    component: lazy(() =>
      import("./components/pages/airportPickup/AirportUpcomingPickupsPage")
    ),
  },
  {
    // Authenticated greeter route (requires login, shows sidenav)
    path: "/greeter/airport-pickups/:websiteId/:airportCode?",
    component: lazy(() =>
      import("./components/pages/airportPickup/AirportUpcomingPickupsPage")
    ),
  },
  // {
  //   path: "/companies",
  //   component: lazy(() =>
  //     import("./components/pages/clients/company/ClientCompaniesPage.tsx")
  //   ),
  // },
  {
    path: "/company-registration/:id?",
    isPublic: true,
    component: lazy(() =>
      import("./components/pages/authentication/RegisterSupplier.jsx")
    ),
    layout: LayoutBlank,
  },
  {
    path: "/car-owner-registration/:id?",
    isPublic: true,
    component: lazy(() =>
      import("./components/pages/registration/RegisterCarOwner.jsx")
    ),
    layout: LayoutBlank,
  },
  {
    path: "/driver-check/:id?",
    component: lazy(() =>
      import("./components/pages/booking/DriverBookingInfoPage.jsx")
    ),
    isPublic: true,
    layout: LayoutBlank,
  },
  {
    path: "/payments",
    component: lazy(() =>
      import("./components/pages/payments/PaymentsPage.jsx").then(module => ({
        default: (props) => <module.default {...props} role="Administrator" />
      }))
    ),
  },
  {
    path: "/payments-driver",
    component: lazy(() =>
      import("./components/pages/payments/PaymentsPage.jsx").then(module => ({
        default: (props) => <module.default {...props} role="Driver" />
      }))
    ),
  },
  {
    path: "/payments-client",
    component: lazy(() =>
      import("./components/pages/payments/PaymentsPage.jsx").then(module => ({
        default: (props) => <module.default {...props} role="Client" />
      }))
    ),
  },
  {
    path: "/payouts-outstanding",
    component: lazy(() =>
      import("./components/pages/payments/OutstandingPayments.jsx")
    ),
  },
  {
    path: "/payins-outstanding",
    component: lazy(() =>
      import("./components/pages/payments/OutstandingClientPayments.jsx")
    ),
  },
  {
    path: "/admin-settings",
    component: lazy(() => import("./components/pages/admin/Admin.jsx")),
  },
  {
    path: "/admin/node-diagnostics",
    component: lazy(() => import("./components/pages/admin/NodeDiagnostics.jsx")),
  },
  {
    path: "/my-profile",
    component: lazy(() =>
      import("./components/pages/authentication/profile/MyProfilePage.jsx")
    ),
  },
  {
    path: "/login",
    isPublic: true,
    component: lazy(() =>
      import("./components/pages/authentication/Login.jsx")
    ),
    layout: LayoutBlank,
  },
  {
    path: "/reset-password/:email?",
    isPublic: true,
    component: lazy(() => import("./components/pages/authentication/RequestResetPasswordPage")),
    layout: LayoutBlank,
  },
  {
    path: "/setpassword/:token?",
    isPublic: true,
    component: lazy(() => import("./components/pages/authentication/SetPasswordPage")),
    layout: LayoutBlank,
  },
  {
    path: "/delete-account",
    isPublic: true,
    component: lazy(() => import("./components/pages/authentication/DeleteAccount.tsx")),
    layout: LayoutBlank,
  },
  {
    path: "/page-2",
    component: lazy(() => import("./components/pages/authentication/Login")),
  },
  {
    path: "/overview",
    component: lazy(() => import("./components/pages/dispatch/bookingStats/Overview.jsx")),
  },
  {
    path: "/drivers",
    component: lazy(() => import("./components/pages/adminDriver/DriversPage.jsx")),
  },
  {
    path: "/pickup-instructions",
    component: lazy(() => import("./components/pages/pickupInstructions/PickupInstructionsPageV2.jsx")),
  },
  {
    path: "/pickup-instructions-v2",
    component: lazy(() => import("./components/pages/pickupInstructions/PickupInstructionsPageV2.jsx")),
  },
  {
    path: "/prices",
    component: lazy(() => import("./components/pages/pricing/pricetable/PricePlansTablePage")),
  },
  {
    path: "/price-editor",
    component: lazy(() => import("./components/pages/pricing/price-editor/PriceEditorPage")),
  },
  {
    path: "/edit-prices",
    component: lazy(() => import("./components/pages/pricing/EditPrices")),
  },
  {
    path: "/services",
    component: lazy(() => import("./components/pages/services/Services")),
  },
  {
    path: "/import-bookings",
    component: lazy(() => import("./components/pages/booking/ImportBookingPage.jsx")),
  },
  {
    path: "/websites/:websiteId?",
    component: lazy(() => import("./components/pages/website/admin/WebsitePage.jsx")),
  },
  {
    path: "/admin/websites-management",
    component: lazy(() => import("./components/pages/website/superAdmin/WebsitesManagement.jsx")),
  },
  {
    path: "/rules",
    component: lazy(() => import("./components/pages/rules/RulesPage")),
  },
  {
    path: "/price-assistant",
    component: lazy(() => import("./components/pages/pricing/comparison/PriceComparisonPage")),
  },
  {
    path: "/price-missing",
    component: lazy(() => import("./components/pages/pricing/comparison/no-price/NoPriceRoutesPage")),
  },
  {
    path: "/region-missing",
    component: lazy(() => import("./components/pages/pricing/comparison/no-region/RoutesNoRegionPage")),
  },
  {
    path: "/stats-by-country",
    component: lazy(() => import("./components/pages/pricing/comparison/per-countries/RequestsPerCountryPage")),
  },
  {
    path: "/price-requests",
    component: lazy(() => import("./components/pages/pricing/comparison/requests/RequestsPage")),
  },
  {
    path: "/request-conversion",
    component: lazy(() => import("./components/pages/reports/PriceRequestInsightsReport")),
  },
  {
    path: "/price-setup",
    component: lazy(() => import("./components/pages/pricing/comparison/PriceSetupPage")),
  },
  {
    path: "/nested-regions",
    component: lazy(() => import("./components/pages/pricing/nested/NestedPricePlansPage")),
  },
  {
    path: "/suggestion-price-driver",
    component: lazy(() => import("./components/pages/pricing/suggestionPrice/DriverCoveragePage")),
  },
  {
    path: "/partner-prices",
    component: lazy(() => import("./components/pages/pricing/partnerPrice/PartnerPricesPage")),
  },
  {
    path: "/pricing-requests",
    component: lazy(() => import("./components/pages/pricing/priceStatistics/PriceStatisticsPage")),
  },
  {
    path: "/partner-setup",
    component: lazy(() => import("./components/pages/pricing/partnerPrice/PartnerSetupPage")),
  },

  {
    path: "/suggestion-price-supplier/legacy",
    component: lazy(() => import("./components/pages/pricing/suggestionPrice/SupplierCoveragePage")),
  },
  {
    path: "/suggestion-price-driver/pricing/:websiteId/setup-price",
    component: lazy(() => import("./components/pages/pricing/suggestionPrice/SetupPrice")),
  },
  {
    path: "/suggestion-price-supplier/pricing/:websiteId/setup-price",
    component: lazy(() => import("./components/pages/pricing/suggestionPrice/SetupPrice")),
  },
  {
    path: "/incidents/complaint-directly",
    component: lazy(() => import("./components/pages/incidents/complaint-directly/ComplaintDirectlyPage.jsx")),
  },
  {
    path: "/incidents/complaint-ticket",
    component: lazy(() => import("./components/pages/incidents/complaint-ticket/ComplaintTicketPage.jsx")),
  },
  {
    path: "/incidents/complaint-auto",
    component: lazy(() => import("./components/pages/incidents/complaint-auto-raise/ComplaintAutoRaisePage.jsx")),
  },
  {
    path: "/incidents/penalty-rules",
    component: lazy(() => import("./components/pages/incidents/penalties/PenaltyRulesPage.jsx")),
  },
  {
    path: "/incidents/policies",
    component: lazy(() => import("./components/pages/incidents/PoliciesPage.jsx")),
  },
  {
    path: "/incidents/my",
    component: lazy(() => import("./components/pages/incidents/complaint-common/DriverSupplierIncidentsPage.jsx")),
  },
  {
    path: "/incidents/:id?",
    component: lazy(() => import("./components/pages/incidents/complaint-common/ComplaintDirectlyDetailPage.jsx")),
  },
  {
    path: "/reviews",
    component: lazy(() => import("./components/pages/reports/ReviewsPage.tsx")),
    requiresTravelThruWebsite: true,
  },
  {
    path: "/support/conversations/:id?",
    component: lazy(() => import("./components/pages/support/conversations/SupportConversationsPage")),
  },
  {
    path: "/support/calls",
    component: lazy(() => import("./components/pages/support/calls/CallsPage")),
  },
  {
    path: "/support/sms",
    component: lazy(() => import("./components/pages/support/sms/SmsLogPage")),
  },
  {
    path: "/support/notifications",
    component: lazy(() => import("./components/pages/support/notifications/NotificationsPage")),
  },
];
